import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Radio, Play, Pin, Loader2, X, Video, Calendar, FileText,
  Link as LinkIcon, PlusCircle, MinusCircle,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WEBINARS_ADMIN } from "@/constants/testIds";

const EMPTY_FORM = {
  id: null,
  title: "",
  description: "",
  tags: "",
  vimeo_id: "",
  live_url: "",
  thumbnail_url: "",
  duration_seconds: "",
  status: "past",
  kind: "webinar",
  is_pinned: false,
  scheduled_at: "",
  recorded_at: "",
  notes: "",
  resources: [],
};

function toDatetimeLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function fromDatetimeLocal(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

export default function AdminWebinars() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/webinars/admin/all");
      setItems(r.data.items || []);
    } catch {
      toast.error("Couldn't load webinars");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (w) => {
    setForm({
      id: w.id,
      title: w.title || "",
      description: w.description || "",
      tags: (w.tags || []).join(", "),
      vimeo_id: w.vimeo_id || "",
      live_url: w.live_url || "",
      thumbnail_url: w.thumbnail_url || "",
      duration_seconds: w.duration_seconds || "",
      status: w.status || "past",
      kind: w.kind || "webinar",
      is_pinned: !!w.is_pinned,
      scheduled_at: toDatetimeLocal(w.scheduled_at),
      recorded_at: toDatetimeLocal(w.recorded_at),
      notes: w.notes || "",
      resources: (w.resources || []).map((r) => ({ ...r, _uid: crypto.randomUUID() })),
    });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setForm(EMPTY_FORM); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      vimeo_id: form.vimeo_id.trim() || null,
      live_url: form.live_url.trim() || null,
      thumbnail_url: form.thumbnail_url.trim() || null,
      duration_seconds: form.duration_seconds ? parseInt(form.duration_seconds, 10) : null,
      status: form.status,
      kind: form.kind,
      is_pinned: !!form.is_pinned,
      scheduled_at: fromDatetimeLocal(form.scheduled_at),
      recorded_at: fromDatetimeLocal(form.recorded_at),
      notes: form.notes,
      resources: (form.resources || [])
        .filter((r) => r.url && r.url.trim())
        .map(({ _uid, ...rest }) => rest),  // strip client-only key
    };
    try {
      if (form.id) {
        await api.patch(`/webinars/${form.id}`, payload);
        toast.success("Webinar updated");
      } else {
        await api.post("/webinars", payload);
        toast.success("Webinar created");
      }
      closeForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleLive = async (w) => {
    try {
      const r = await api.post(`/webinars/${w.id}/set-live`);
      toast.success(r.data.status === "live" ? "Marked live" : "Marked past");
      await load();
    } catch {
      toast.error("Couldn't update status");
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete "${w.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/webinars/${w.id}`);
      toast.success("Webinar deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const addResource = () => setForm((f) => ({
    ...f,
    resources: [...(f.resources || []), { _uid: crypto.randomUUID(), title: "", url: "" }],
  }));

  const updateResource = (idx, key, val) => setForm((f) => ({
    ...f,
    resources: f.resources.map((r, i) => i === idx ? { ...r, [key]: val } : r),
  }));

  const removeResource = (idx) => setForm((f) => ({
    ...f,
    resources: f.resources.filter((_, i) => i !== idx),
  }));

  const grouped = useMemo(() => {
    const live = items.filter((x) => x.status === "live");
    const draft = items.filter((x) => x.status === "draft");
    const past = items.filter((x) => x.status === "past");
    return { live, draft, past };
  }, [items]);

  const statusBadge = (s) => ({
    live: "bg-rose-100 text-rose-700 border-rose-200",
    draft: "bg-amber-100 text-amber-700 border-amber-200",
    past: "bg-slate-100 text-slate-700 border-slate-200",
  }[s] || "bg-slate-100 text-slate-700 border-slate-200");

  return (
    <div data-testid={WEBINARS_ADMIN.root} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight">
            Video Library
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage live sessions, upcoming schedule, and the past-webinars library.
          </p>
        </div>
        <Button
          onClick={openNew}
          data-testid={WEBINARS_ADMIN.newButton}
          className="bg-blue-900 hover:bg-blue-800 text-white h-11"
        >
          <Plus size={16} className="mr-2" /> New webinar
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          {["live", "draft", "past"].map((section) => {
            const list = grouped[section];
            if (!list?.length) return null;
            const label = section === "live" ? "Live now"
              : section === "draft" ? "Upcoming & drafts"
              : "Library (past webinars, recaps & videos)";
            return (
              <section key={section}>
                <h2 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">
                  {label} · {list.length}
                </h2>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
                  {list.map((w) => (
                    <div
                      key={w.id}
                      data-testid={`${WEBINARS_ADMIN.row}-${w.id}`}
                      className="flex flex-col md:flex-row md:items-center gap-3 p-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${statusBadge(w.status)}`}>
                            {w.status}
                          </span>
                          <span data-testid={`admin-webinar-kind-${w.id}`} className="inline-flex items-center text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">
                            {w.kind || "webinar"}
                          </span>
                          {w.is_pinned && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                              <Pin size={10} /> Featured
                            </span>
                          )}
                          <h3 className="font-display font-bold text-base text-slate-900 truncate">
                            {w.title}
                          </h3>
                        </div>
                        <div className="mt-1 text-xs text-slate-500 font-mono">
                          {w.vimeo_id ? `vimeo: ${w.vimeo_id}` : "no video"} ·
                          {" "}
                          {(w.tags || []).length} tags · {w.view_count || 0} views
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          onClick={() => toggleLive(w)}
                          data-testid={`${WEBINARS_ADMIN.toggleLive}-${w.id}`}
                          variant="outline"
                          size="sm"
                          className={w.status === "live"
                            ? "border-rose-400 text-rose-700 hover:bg-rose-50"
                            : "border-slate-300 text-slate-700 hover:bg-slate-50"}
                        >
                          <Radio size={13} className="mr-1.5" />
                          {w.status === "live" ? "End live" : "Go live"}
                        </Button>
                        <Button
                          onClick={() => openEdit(w)}
                          data-testid={`${WEBINARS_ADMIN.editButton}-${w.id}`}
                          variant="outline"
                          size="sm"
                          className="border-slate-300"
                        >
                          <Pencil size={13} className="mr-1.5" /> Edit
                        </Button>
                        <Button
                          onClick={() => remove(w)}
                          data-testid={`${WEBINARS_ADMIN.deleteButton}-${w.id}`}
                          variant="outline"
                          size="sm"
                          className="border-rose-300 text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}

          {items.length === 0 && (
            <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
              <Video size={28} className="mx-auto text-slate-400 mb-3" />
              <p className="text-sm text-slate-600 font-semibold">No webinars yet.</p>
              <p className="text-xs text-slate-500 mt-1">Create your first session using the button above.</p>
            </div>
          )}
        </>
      )}

      {/* Form modal */}
      {showForm && (
        <div
          data-testid={WEBINARS_ADMIN.formModal}
          className="fixed inset-0 z-[70] bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
          onClick={closeForm}
        >
          <div className="min-h-screen flex items-start justify-center py-8 px-4">
            <form
              onSubmit={submit}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-display font-bold text-lg text-slate-900">
                  {form.id ? "Edit webinar" : "New webinar"}
                </h3>
                <button
                  type="button"
                  onClick={closeForm}
                  className="w-8 h-8 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Title */}
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                    Title *
                  </label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid={WEBINARS_ADMIN.formTitle}
                    required
                    className="h-11"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid={WEBINARS_ADMIN.formDescription}
                    rows={3}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                    Tags (comma-separated)
                  </label>
                  <Input
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    data-testid={WEBINARS_ADMIN.formTags}
                    placeholder="Timeframe Correlation, Entries, Risk Management"
                    className="h-11"
                  />
                </div>

                {/* Video */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                      Vimeo video ID or URL
                    </label>
                    <Input
                      value={form.vimeo_id}
                      onChange={(e) => setForm({ ...form, vimeo_id: e.target.value })}
                      data-testid={WEBINARS_ADMIN.formVimeo}
                      placeholder="123456789 or https://vimeo.com/123456789"
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                      Live stream URL (for live sessions)
                    </label>
                    <Input
                      value={form.live_url}
                      onChange={(e) => setForm({ ...form, live_url: e.target.value })}
                      data-testid={WEBINARS_ADMIN.formLiveUrl}
                      placeholder="https://vimeo.com/event/... or Zoom link"
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                      Duration (seconds)
                    </label>
                    <Input
                      type="number"
                      value={form.duration_seconds}
                      onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })}
                      data-testid={WEBINARS_ADMIN.formDuration}
                      placeholder="e.g. 3600 = 1 hour"
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                      Recorded at
                    </label>
                    <Input
                      type="datetime-local"
                      value={form.recorded_at}
                      onChange={(e) => setForm({ ...form, recorded_at: e.target.value })}
                      data-testid={WEBINARS_ADMIN.formRecordedAt}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                      Scheduled at (upcoming)
                    </label>
                    <Input
                      type="datetime-local"
                      value={form.scheduled_at}
                      onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                      data-testid={WEBINARS_ADMIN.formScheduledAt}
                      className="h-11"
                    />
                  </div>
                </div>

                {/* Thumbnail */}
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                    Custom thumbnail URL (optional — defaults to Vimeo)
                  </label>
                  <Input
                    value={form.thumbnail_url}
                    onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
                    data-testid={WEBINARS_ADMIN.formThumbnail}
                    className="h-11"
                  />
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">Type</label>
                  <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    data-testid="admin-webinar-form-kind"
                    className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none">
                    <option value="webinar">Webinar</option>
                    <option value="recap">Recap</option>
                    <option value="video">Video (one-off)</option>
                  </select>
                </div>

                {/* Status + pinned */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                      Status
                    </label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      data-testid={WEBINARS_ADMIN.formStatus}
                      className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                    >
                      <option value="past">Past (in library)</option>
                      <option value="draft">Upcoming / draft</option>
                      <option value="live">Live now</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.is_pinned}
                        onChange={(e) => setForm({ ...form, is_pinned: e.target.checked })}
                        data-testid={WEBINARS_ADMIN.formPinned}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-slate-700">
                        Feature in &quot;Pinned&quot; row
                      </span>
                    </label>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">
                    <FileText size={11} className="inline mr-1" /> Session notes (shown on the detail modal)
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid={WEBINARS_ADMIN.formNotes}
                    rows={4}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-mono focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                  />
                </div>

                {/* Attached resources */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                      <LinkIcon size={11} className="inline mr-1" /> Attached resources
                    </label>
                    <button
                      type="button"
                      onClick={addResource}
                      data-testid={WEBINARS_ADMIN.formAddResource}
                      className="text-xs font-semibold text-blue-800 hover:text-blue-700 inline-flex items-center gap-1"
                    >
                      <PlusCircle size={12} /> Add link
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(form.resources || []).map((r, i) => (
                      <div key={r._uid || i} className="flex gap-2">
                        <Input
                          value={r.title || ""}
                          onChange={(e) => updateResource(i, "title", e.target.value)}
                          placeholder="Label"
                          className="h-10 flex-[0_0_35%]"
                        />
                        <Input
                          value={r.url || ""}
                          onChange={(e) => updateResource(i, "url", e.target.value)}
                          placeholder="https://…"
                          className="h-10 flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => removeResource(i)}
                          className="text-slate-400 hover:text-rose-500"
                          aria-label="Remove"
                        >
                          <MinusCircle size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeForm}
                  disabled={saving}
                  className="border-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid={WEBINARS_ADMIN.formSave}
                  disabled={saving}
                  className="bg-blue-900 hover:bg-blue-800 text-white"
                >
                  {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                  {form.id ? "Save changes" : "Create webinar"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
